// src/ui/primitives.jsx: gedeelde UI building blocks
import React from "react";
import { createPortal } from "react-dom";

export const FW = {
  navy: "#221659",
  navy2: "#2C1F66",
  yellow: "#F9EC31",
  mint: "#00E69E",
  cyan: "#2ADDE6",
  cream: "#FDFCFA",
  beige: "#F9F6EF",
  beige2: "#EFE8D3",
  taupe: "#D8CBB1",
  gray50: "#F5F5F5",
  gray100: "#EAEAEB",
  gray500: "#737373",
  white: "#FFFFFF",
};

// Eenmalige injectie van de spin-keyframe voor laad-iconen (ri-loader-4-line).
if (typeof document !== "undefined" && !document.getElementById("fw-spin-style")) {
  const el = document.createElement("style");
  el.id = "fw-spin-style";
  el.textContent = "@keyframes fw-spin{to{transform:rotate(360deg)}}";
  document.head.appendChild(el);
}

export const FwButton = ({ variant = "primary", children, onClick, href, arrow = true, icon, loading = false, disabled, style = {}, small = false, ...rest }) => {
  const padding = small ? "10px 16px" : "14px 22px";
  const fontSize = small ? 15 : 16;
  const bases = {
    primary:   { background: FW.yellow, color: FW.navy, border: 0 },
    navy:      { background: FW.navy,   color: "#fff",  border: 0 },
    secondary: { background: FW.white,  color: FW.navy, border: `1px solid ${FW.gray100}` },
    ghost:     { background: "transparent", color: FW.navy, border: 0 },
    danger:    { background: "transparent", color: "#B0384B", border: `1px solid #E8C6CD` },
  };
  const arrowBg = variant === "navy" ? FW.yellow : FW.navy;
  const arrowFg = variant === "navy" ? FW.navy : (variant === "primary" ? FW.yellow : "#fff");

  const gedeeldeStyle = {
    display: "inline-flex", alignItems: "center", gap: 10,
    padding, borderRadius: 999, fontWeight: 700, fontSize, cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "var(--font-sans)", lineHeight: 1.1,
    opacity: disabled ? 0.5 : 1,
    transition: "transform .15s ease, box-shadow .15s ease",
    textDecoration: "none",
    ...bases[variant], ...style,
  };
  const hoverHandlers = {
    onMouseDown: (e) => !disabled && (e.currentTarget.style.transform = "scale(0.96)"),
    onMouseUp:   (e) => (e.currentTarget.style.transform = "scale(1)"),
    onMouseLeave: (e) => (e.currentTarget.style.transform = "scale(1)"),
  };
  const inhoud = (
    <>
      {(loading || icon) && (
        <i className={loading ? "ri-loader-4-line" : icon}
          style={{ fontSize: 18, ...(loading ? { animation: "fw-spin 0.7s linear infinite" } : null) }} />
      )}
      {children}
      {arrow && variant !== "ghost" && (
        <span style={{
          display: "inline-grid", placeItems: "center",
          width: 26, height: 26, borderRadius: 999,
          background: arrowBg, color: arrowFg, fontSize: 14,
        }}>→</span>
      )}
    </>
  );

  // Als href is gegeven, renderen we een echte <a>, de browser pakt
  // dan zelf de protocol-handler op (mailto:, tel:, externe URL).
  if (href && !disabled) {
    return (
      <a href={href} onClick={onClick} style={gedeeldeStyle} {...hoverHandlers} {...rest}>
        {inhoud}
      </a>
    );
  }

  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled}
      style={gedeeldeStyle} {...hoverHandlers} {...rest}>
      {inhoud}
    </button>
  );
};

export const FwCard = ({ tint, children, style = {}, padded = true, ...rest }) => {
  const tints = { mint: FW.mint, cyan: FW.cyan, yellow: FW.yellow, cream: FW.cream, navy: FW.navy };
  return (
    <div style={{
      background: tint ? tints[tint] : FW.white,
      color: tint === "navy" ? "#fff" : FW.navy,
      borderRadius: 24,
      padding: padded ? 32 : 0,
      boxShadow: "0 4px 12px rgba(0,0,0,.06)",
      ...style
    }} {...rest}>{children}</div>
  );
};

export const FwInput = React.forwardRef(({ label, hint, error, suffix, style = {}, containerStyle = {}, ...rest }, ref) => (
  <label style={{ display: "block", ...containerStyle }}>
    {label && <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 8 }}>{label}</div>}
    <div style={{
      display: "flex", alignItems: "center",
      border: `1.5px solid ${error ? "#D4526E" : FW.gray100}`,
      background: FW.white,
      borderRadius: 14,
      padding: "0 16px",
      transition: "border-color .15s",
    }}>
      <input ref={ref} {...rest} style={{
        flex: 1, border: 0, background: "transparent", padding: "14px 0",
        fontFamily: "var(--font-sans)", fontSize: 16, color: FW.navy, outline: "none",
        ...style
      }} />
      {suffix && <span style={{ color: FW.gray500, fontSize: 14, fontWeight: 600 }}>{suffix}</span>}
    </div>
    {hint && !error && <div style={{ fontSize: 13, color: FW.gray500, marginTop: 6 }}>{hint}</div>}
    {error && <div style={{ fontSize: 13, color: "#B0384B", marginTop: 6 }}>{error}</div>}
  </label>
));

export const FwTextarea = ({ label, hint, value, onChange, placeholder, rows = 4, style = {} }) => (
  <label style={{ display: "block" }}>
    {label && <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 8 }}>{label}</div>}
    <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} style={{
      width: "100%", border: `1.5px solid ${FW.gray100}`, borderRadius: 14,
      padding: "14px 16px", fontFamily: "var(--font-sans)", fontSize: 16, color: FW.navy,
      background: FW.white, outline: "none", resize: "vertical", lineHeight: 1.4,
      ...style
    }} />
    {hint && <div style={{ fontSize: 13, color: FW.gray500, marginTop: 6 }}>{hint}</div>}
  </label>
);

export const FwSelect = ({
  value,
  onChange,
  options,
  placeholder = "Maak een keuze",
  clearable = false,
  ariaLabel,
  style = {},
}) => {
  const [open, setOpen] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const wrapperRef = React.useRef(null);

  const items = (options || []).map(o => typeof o === "string" ? { value: o, label: o } : o);
  const huidig = items.find(i => i.value === value);
  const label = huidig?.label ?? placeholder;

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const actief = open || focused;
  const leeg = value == null || value === "";

  return (
    <div ref={wrapperRef} style={{ position: "relative", ...style }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen(o => !o)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: "100%", minHeight: 48,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          padding: "0 14px", borderRadius: 14,
          border: `1.5px solid ${actief ? FW.navy : FW.gray100}`,
          background: FW.white,
          fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 500,
          color: leeg ? FW.navy2 : FW.navy,
          outline: "none", cursor: "pointer", textAlign: "left",
          boxShadow: actief ? `0 0 0 3px rgba(46, 24, 71, 0.12)` : "none",
          transition: "border-color .15s ease, box-shadow .15s ease",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <i
          className="ri-arrow-down-s-line"
          style={{
            fontSize: 18, color: FW.navy2, flexShrink: 0,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform .15s ease",
          }}
        />
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 50,
            background: FW.white, border: `1.5px solid ${FW.gray100}`, borderRadius: 14,
            padding: 6, maxHeight: 280, overflowY: "auto",
            boxShadow: "0 8px 24px rgba(0,0,0,.10)",
          }}
        >
          {clearable && (
            <FwSelectOption actief={leeg} onClick={() => { onChange(null); setOpen(false); }}>
              {placeholder}
            </FwSelectOption>
          )}
          {items.map(item => (
            <FwSelectOption
              key={String(item.value)}
              actief={item.value === value}
              onClick={() => { onChange(item.value); setOpen(false); }}
            >{item.label}</FwSelectOption>
          ))}
        </div>
      )}
    </div>
  );
};

const FwSelectOption = ({ actief, onClick, children }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      type="button"
      role="option"
      aria-selected={actief}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "block", width: "100%", textAlign: "left",
        padding: "10px 12px", border: 0, borderRadius: 10,
        background: actief ? FW.cream : (hover ? "rgba(46, 24, 71, 0.05)" : "transparent"),
        color: FW.navy, fontFamily: "var(--font-sans)", fontSize: 15,
        fontWeight: actief ? 700 : 500,
        cursor: "pointer",
      }}
    >{children}</button>
  );
};

export const FwChip = ({ children, tint = "cream", active, onClick, icon, style = {} }) => {
  const bg = active
    ? FW.navy
    : (tint === "mint"   ? "#E7FFF2"
      : tint === "cyan"   ? "#E5FBFD"
      : tint === "yellow" ? "#FEF9B3"
      : tint === "navy"   ? FW.navy
      : FW.cream);
  const fg = active || tint === "navy" ? "#fff" : FW.navy;
  return (
    <span onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "8px 14px", borderRadius: 999, background: bg, color: fg,
      fontSize: 14, fontWeight: 600, cursor: onClick ? "pointer" : "default",
      border: `1px solid ${active ? FW.navy : "transparent"}`,
      transition: "background-color .15s, color .15s, border-color .15s",
      ...style
    }}>
      {icon && <i className={icon} style={{ fontSize: 14 }} />}
      {children}
    </span>
  );
};

export const StepProgress = ({ steps, current, onJumpTo }) => (
  <div className="stepper-root" style={{ display: "flex", alignItems: "center", gap: 8 }}>
    {steps.map((s, i) => {
      const klikbaar = !!onJumpTo && i < current;
      return (
        <React.Fragment key={i}>
          <div
            onClick={klikbaar ? () => onJumpTo(i) : undefined}
            role={klikbaar ? "button" : undefined}
            tabIndex={klikbaar ? 0 : undefined}
            onKeyDown={klikbaar ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onJumpTo(i); } } : undefined}
            title={klikbaar ? `Terug naar: ${s}` : undefined}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              opacity: i > current ? 0.5 : 1,
              cursor: klikbaar ? "pointer" : "default",
              padding: "4px 6px", margin: "-4px -6px", borderRadius: 8,
              transition: "background .15s ease",
            }}
            onMouseEnter={klikbaar ? (e) => { e.currentTarget.style.background = FW.beige2; } : undefined}
            onMouseLeave={klikbaar ? (e) => { e.currentTarget.style.background = "transparent"; } : undefined}>
            <div style={{
              width: 24, height: 24, borderRadius: 999,
              background: i < current ? FW.taupe : i === current ? FW.navy : FW.gray100,
              color: i <= current ? (i === current ? "#fff" : FW.navy) : FW.gray500,
              display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700,
            }}>{i < current ? "✓" : i + 1}</div>
            <span
              className={i === current ? "stepper-label stepper-label-current" : "stepper-label"}
              style={{ fontSize: 14, fontWeight: i === current ? 700 : 500, color: FW.navy }}>
              {s}
            </span>
          </div>
          {i < steps.length - 1 && <span className="stepper-connector" style={{ width: 20, height: 2, background: i < current ? FW.taupe : FW.gray100 }} />}
        </React.Fragment>
      );
    })}
  </div>
);

export const FwInlineBanner = ({ type = "warning", children, onDismiss, style = {} }) => {
  const configs = {
    warning: { bg: "#FFF3CD", border: "#FFD25A", icon: "ri-alert-line",         color: FW.navy },
    error:   { bg: "#FEE8EC", border: "#E8C6CD", icon: "ri-error-warning-line", color: "#B0384B" },
    info:    { bg: "#E7FFF2", border: "#00E69E", icon: "ri-information-line",   color: FW.navy },
  };
  const c = configs[type] || configs.warning;
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 10,
      padding: "12px 16px", borderRadius: 12,
      background: c.bg, border: `1.5px solid ${c.border}`,
      fontSize: 14, color: c.color, lineHeight: 1.45,
      ...style
    }}>
      <i className={c.icon} style={{ fontSize: 16, marginTop: 1, flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{children}</span>
      {onDismiss && (
        <button onClick={onDismiss} style={{
          background: "transparent", border: 0, cursor: "pointer",
          color: c.color, fontSize: 18, padding: "0 0 0 8px", lineHeight: 1, flexShrink: 0,
        }}>×</button>
      )}
    </div>
  );
};

export const StepHeader = ({ tag, title, sub, onBack, vorm, perspectief }) => (
  <div style={{ marginBottom: 28 }}>
    {(onBack || tag || vorm || perspectief) && (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        {onBack && (
          <button
            onClick={onBack}
            aria-label="Terug naar vorige stap"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "4px 12px 4px 9px", borderRadius: 6,
              background: FW.white, color: FW.navy, border: `1px solid ${FW.taupe}`,
              fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
              fontFamily: "var(--font-sans)", cursor: "pointer",
              transition: "background .15s, border-color .15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = FW.beige2; }}
            onMouseLeave={e => { e.currentTarget.style.background = FW.white; }}
          >
            <i className="ri-arrow-left-line" style={{ fontSize: 14 }} /> Terug
          </button>
        )}
        {tag && (() => {
          // Funnel-kleuren voor herkenbaarheid: film/av = mint (groen),
          // festival = cyan (blauw). Tekst blijft navy voor contrast.
          const isFestivalTag = /festival/i.test(tag);
          const isFilmAvTag = /film\/av/i.test(tag);
          const funnelBg = isFestivalTag ? FW.cyan : isFilmAvTag ? FW.mint : FW.navy;
          const funnelFg = (isFestivalTag || isFilmAvTag) ? FW.navy : FW.yellow;
          return (
            <div style={{ padding: "4px 10px", borderRadius: 6,
              background: funnelBg, color: funnelFg, fontSize: 12, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: 0.5 }}>{tag}</div>
          );
        })()}
        {vorm && <VormBadge vorm={vorm} />}
        {perspectief && <PerspectiveBadge perspectief={perspectief} />}
      </div>
    )}
    <h2 className="step-h2" style={{ fontSize: 36, fontWeight: 700, lineHeight: 1.15, margin: "0 0 10px", letterSpacing: "-0.01em" }}>{title}</h2>
    {sub && (Array.isArray(sub)
      ? (
        <div className="step-sub" style={{ fontSize: 17, color: FW.navy2, maxWidth: 680, lineHeight: 1.45 }}>
          {sub.map((para, i) => (
            <p key={i} style={{ margin: i < sub.length - 1 ? "0 0 10px" : 0, fontSize: 17 }}>{para}</p>
          ))}
        </div>
      )
      : <p className="step-sub" style={{ fontSize: 17, color: FW.navy2, maxWidth: 680, margin: 0, lineHeight: 1.45 }}>{sub}</p>
    )}
  </div>
);

export const VormBadge = ({ vorm }) => {
  const isZzp = vorm === "zzp";
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 10px", borderRadius: 6,
      background: FW.navy, color: FW.yellow, fontSize: 12, fontWeight: 700,
      textTransform: "uppercase", letterSpacing: 0.5,
      whiteSpace: "nowrap",
    }}>
      <i className={isZzp ? "ri-user-line" : "ri-briefcase-line"} style={{ fontSize: 13 }} />
      {isZzp ? "ZZP" : "Loondienst"}
    </div>
  );
};

// Used for film/AV-productie to distinguish the worker (zzp) from the client.
// Festival uses VormBadge instead (zzp vs loondienst).
export const PerspectiveBadge = ({ perspectief }) => {
  const isClient = perspectief === "opdrachtgever";
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 10px", borderRadius: 6,
      background: FW.navy, color: FW.yellow, fontSize: 12, fontWeight: 700,
      textTransform: "uppercase", letterSpacing: 0.5,
      whiteSpace: "nowrap",
    }}>
      <i className={isClient ? "ri-building-line" : "ri-user-line"} style={{ fontSize: 13 }} />
      {isClient ? "Opdrachtgever" : "ZZP"}
    </div>
  );
};

export const NiveauBadge = ({ niveau }) => (
  <span style={{
    display: "inline-grid", placeItems: "center",
    minWidth: 32, padding: "4px 10px", borderRadius: 8,
    background: FW.navy, color: FW.yellow,
    fontSize: 14, fontWeight: 700,
  }}>{niveau}</span>
);

export const AsideCard = ({ children }) => (
  <div className="aside-card" style={{ background: "#E7FFF2", borderRadius: 20, padding: 22, position: "sticky", top: 20 }}>
    {children}
  </div>
);

// Compacte rij bovenaan de resultaat-pagina: type, vorm (festival), rol·dep, niveau.
// `vorm` alleen tonen bij filmfestivals.
export const ResultaatSamenvatting = ({ isFestival, vorm, rol, dep, niveau }) => {
  const chipLabel = { fontSize: 12, color: FW.gray500, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 };
  const chipValue = { fontSize: 14, fontWeight: 700, color: FW.navy };
  const chipSep   = { color: FW.gray500 };
  return (
    <div className="result-samenvatting" style={{
      display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap",
      padding: "14px 18px", marginBottom: 20,
      background: FW.cream, border: `1.5px solid ${FW.gray100}`, borderRadius: 14,
    }}>
      <div style={chipLabel}>Type</div>
      <div style={chipValue}>{isFestival ? "Filmfestival" : "Film/AV-productie"}</div>
      {isFestival && vorm && (
        <>
          <span style={chipSep}>·</span>
          <div style={chipLabel}>Vorm</div>
          <div style={chipValue}>{vorm === "loondienst" ? "Loondienst" : "ZZP"}</div>
        </>
      )}
      <span style={chipSep}>·</span>
      <div style={chipLabel}>Rol</div>
      <div style={chipValue}>{rol}</div>
      <span style={chipSep}>·</span>
      <div style={chipLabel}>Departement</div>
      <div style={chipValue}>{dep}</div>
      {niveau && (
        <>
          <span style={chipSep}>·</span>
          <div style={chipLabel}>{isFestival ? "Schaal" : "Niveau"}</div>
          <NiveauBadge niveau={niveau} />
        </>
      )}
    </div>
  );
};

// Robuust naar klembord kopiëren: valt terug op een tijdelijk textarea +
// execCommand wanneer navigator.clipboard ontbreekt (non-secure context, bijv.
// http op een LAN-IP).
async function kopieerNaarKlembord(tekst) {
  try {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(tekst);
      return true;
    }
  } catch { /* val terug op execCommand */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = tekst;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// Herbruikbaar deel-/downloadblok onderaan de resultaatpagina's (AV-productie
// en festival). Bouwt zelf de deelbare link uit `state`; de e-mailknop kopieert
// de meegegeven `emailText` naar het klembord.
export const DeelResultaat = ({
  state,
  emailText,
  onDownload,
  titel = "Download hier je openingsbod",
  sub = "Deel via een link, kopieer een tekstbestand of download als Word-document.",
  emailLabel = "Kopieer tekstbestand",
  downloadLabel = "Download als Word",
}) => {
  const [sharing, setSharing] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const kopieer = async (tekst, setFlag) => {
    const ok = await kopieerNaarKlembord(tekst);
    if (ok) {
      setFlag(true);
      setTimeout(() => setFlag(false), 2000);
    }
  };
  const shareLink = () => {
    const json = btoa(unescape(encodeURIComponent(JSON.stringify(state))));
    const url = `${location.origin}${location.pathname}#berekening=${json}`;
    kopieer(url, setSharing);
  };
  return (
    <FwCard padded={false} style={{ marginTop: 24, padding: 22, background: FW.beige2 }} className="hide-print">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: "1 1 260px" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: FW.navy, marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <i className="ri-download-line" /> {titel}
          </div>
          <div style={{ fontSize: 14, color: FW.navy2, lineHeight: 1.4 }}>{sub}</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <FwButton variant="navy" icon="ri-link" arrow={false} onClick={shareLink}>
            {sharing ? "Link gekopieerd ✓" : "Kopieer deelbare link"}
          </FwButton>
          <FwButton variant="secondary" icon="ri-mail-line" arrow={false} onClick={() => kopieer(emailText, setCopied)}>
            {copied ? "Tekst gekopieerd ✓" : emailLabel}
          </FwButton>
          <FwButton variant="secondary" icon="ri-file-word-2-line" arrow={false} onClick={onDownload}>
            {downloadLabel}
          </FwButton>
        </div>
      </div>
    </FwCard>
  );
};

const TOOLTIP_MARGE = 12;
const TOOLTIP_TUSSENRUIMTE = 6;

export const RolTooltip = ({ tekst, color = FW.gray500, ariaLabel = "Toelichting" }) => {
  const [pos, setPos] = React.useState(null);
  const iconRef = React.useRef(null);
  const tooltipRef = React.useRef(null);
  // Tweede pass: meet werkelijke tooltip-hoogte; flip naar boven als hij niet
  // onder past maar wel boven. Conditioneel op !gemeten om infinite loop te
  // vermijden. Hook MOET boven elke early return staan: Rules of Hooks.
  React.useLayoutEffect(() => {
    if (!pos || pos.gemeten || !tooltipRef.current || !iconRef.current) return;
    const tip = tooltipRef.current;
    const r = iconRef.current.getBoundingClientRect();
    const hoogte = tip.scrollHeight;
    const ruimteOnder = window.innerHeight - r.bottom - TOOLTIP_MARGE - TOOLTIP_TUSSENRUIMTE;
    const ruimteBoven = r.top - TOOLTIP_MARGE - TOOLTIP_TUSSENRUIMTE;
    const naarBoven = hoogte > ruimteOnder && ruimteBoven > ruimteOnder;
    setPos(prev => prev && !prev.gemeten ? {
      ...prev,
      top:    naarBoven ? null : r.bottom + TOOLTIP_TUSSENRUIMTE,
      bottom: naarBoven ? window.innerHeight - r.top + TOOLTIP_TUSSENRUIMTE : null,
      maxH:   naarBoven ? ruimteBoven : ruimteOnder,
      gemeten: true,
    } : prev);
  }, [pos]);
  const bullets = Array.isArray(tekst) && tekst.length > 0 ? tekst : null;
  const heeftTekst = typeof tekst === "string" && tekst.trim().length > 0;
  if (!bullets && !heeftTekst) return null;
  const marge = TOOLTIP_MARGE;
  const tussenruimte = TOOLTIP_TUSSENRUIMTE;
  const toon = (e) => {
    e.stopPropagation();
    if (!iconRef.current) return;
    const r = iconRef.current.getBoundingClientRect();
    // Eerste pass: plaats onder, breedte clamped aan viewport, hoogte nog niet
    // gemeten. useLayoutEffect doet eventueel een tweede pass met flip.
    const maxW = Math.min(bullets ? 360 : 280, window.innerWidth - 2 * marge);
    const left = Math.max(marge, Math.min(r.left, window.innerWidth - maxW - marge));
    const ruimteOnder = window.innerHeight - r.bottom - marge - tussenruimte;
    setPos({
      left, maxW,
      top: r.bottom + tussenruimte, bottom: null,
      maxH: ruimteOnder,
      gemeten: false,
    });
  };
  const verberg = (e) => { e.stopPropagation(); setPos(null); };
  // Tooltip via Portal naar document.body: anders kan een ancestor met een
  // eigen stacking context (sticky AsideCard, transform-parent) de tooltip
  // achter andere vlakken laten vallen ondanks position:fixed.
  // div ipv span omdat we soms een <ul> renderen (invalid in span).
  // pointerEvents: auto zodat lange tooltips scrollbaar zijn als ze in beide
  // richtingen niet helemaal passen: React-portal bubbling houdt onMouseLeave
  // op de wrapper-span correct getriggerd.
  const tooltipNode = pos && createPortal(
    <div ref={tooltipRef} role="tooltip" style={{
      position: "fixed",
      top:    pos.top    ?? "auto",
      bottom: pos.bottom ?? "auto",
      left: pos.left, zIndex: 9999,
      width: "max-content", maxWidth: pos.maxW,
      maxHeight: pos.maxH, overflowY: "auto",
      padding: "10px 12px",
      background: FW.navy, color: FW.white, borderRadius: 10,
      fontSize: 13, fontWeight: 500, lineHeight: 1.4, textAlign: "left",
      // textTransform reset: tooltip mag nooit uppercase erven van een
      // parent label (bijv. 'MINIMUM STARTTARIEF').
      textTransform: "none", letterSpacing: "normal",
      boxShadow: "0 6px 20px rgba(0,0,0,.18)",
      whiteSpace: "normal",
      // visibility tijdens eerste pass: nog onzichtbaar zodat de gebruiker
      // geen kort 'springen' ziet als we naar boven flippen.
      visibility: pos.gemeten ? "visible" : "hidden",
    }}>
      {bullets ? (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {bullets.map((b, i) => (
            <li key={i} style={{ marginBottom: i < bullets.length - 1 ? 4 : 0 }}>{b}</li>
          ))}
        </ul>
      ) : tekst}
    </div>,
    document.body,
  );
  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}
      onMouseEnter={toon} onMouseLeave={verberg}
      onFocus={toon} onBlur={verberg}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); pos ? verberg(e) : toon(e); }}>
      <i ref={iconRef} className="ri-information-line"
        tabIndex={0}
        aria-label={ariaLabel}
        style={{
          fontSize: 16, color, cursor: "help",
          display: "inline-grid", placeItems: "center", outline: "none",
        }} />
      {tooltipNode}
    </span>
  );
};
